/**
 * 可折叠项目列表组件
 * 适合移动端使用，默认折叠显示关键信息，展开显示详情
 * 背景色根据预算使用率渐变着色
 */
import React, { useState } from 'react';
import { Collapse, Progress, Tag, Space, Button, Typography, Divider, Tooltip } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  RightOutlined,
  ExportOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { DashboardService } from '../../services/dashboardService';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Text } = Typography;
const { Panel } = Collapse;

export interface CollapsibleProjectListProps {
  projects: ProjectOverviewData[];
  onViewDetails?: (project: ProjectOverviewData) => void;
  onExport?: (project: ProjectOverviewData) => void;
  onAddData?: (project: ProjectOverviewData) => void;
}

// 项目状态配置
const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin />, text: '进行中' },
  '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined />, text: '待开始' },
  '已暂停': { color: 'warning', icon: <PauseCircleOutlined />, text: '已暂停' }
};

export const CollapsibleProjectList: React.FC<CollapsibleProjectListProps> = ({
  projects,
  onViewDetails,
  onExport,
  onAddData
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  // 格式化金额显示
  const formatMoney = (value: number): string => {
    if (value >= 10000) {
      return `¥${(value / 10000).toFixed(1)}万`;
    }
    return `¥${value.toLocaleString()}`;
  };

  // 渲染折叠面板头部
  const renderPanelHeader = (project: ProjectOverviewData) => {
    const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG['待开始'];
    const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);

    return (
      <Tooltip title={`预算使用: ${budgetColors.percentage.toFixed(1)}% (${budgetColors.statusText})`}>
        <div style={{ width: '100%', paddingRight: 8 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 4
          }}>
            <Text strong style={{ fontSize: 14, flex: 1 }}>{project.name}</Text>
            <Space size={8}>
              <Tag 
                color={statusConfig.color} 
                style={{ fontSize: 11, marginRight: 0 }}
              >
                {statusConfig.text}
              </Tag>
              <Text style={{ fontSize: 12, color: '#1677ff' }}>
                {project.progress}%
              </Text>
            </Space>
          </div>
          <Progress
            percent={project.progress}
            size="small"
            strokeColor={project.progress >= 100 ? '#52c41a' : '#1677ff'}
            showInfo={false}
            style={{ marginBottom: 0 }}
          />
        </div>
      </Tooltip>
    );
  };

  // 渲染折叠面板内容
  const renderPanelContent = (project: ProjectOverviewData) => {
    const health = DashboardService.calculateProjectHealth(project.budget, project.actualCost);
    const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);

    return (
      <div>
        {/* 预算信息 */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>预算使用</Text>
            <Tag color={budgetColors.status === 'over' ? 'error' : budgetColors.status === 'danger' ? 'warning' : 'default'} style={{ fontSize: 10 }}>
              {budgetColors.statusText}
            </Tag>
          </div>
          <div style={{ marginTop: 4 }}>
            <Progress
              percent={Math.min(health.percentage, 100)}
              size="small"
              strokeColor={health.color}
              format={() => (
                <span style={{ color: health.color, fontSize: 12 }}>
                  {health.percentage.toFixed(1)}%
                </span>
              )}
            />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              fontSize: 12,
              marginTop: 4
            }}>
              <Text type="secondary">
                实际: <span style={{ color: health.color }}>{formatMoney(project.actualCost)}</span>
              </Text>
              <Text type="secondary">
                预算: {formatMoney(project.budget)}
              </Text>
            </div>
          </div>
        </div>

        {/* 项目信息 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginBottom: 12
        }}>
          <Space size={4}>
            <TeamOutlined style={{ color: '#1677ff', fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {project.teamSize} 条记录
            </Text>
          </Space>
          <Space size={4}>
            <CalendarOutlined style={{ color: '#52c41a', fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {project.daysRemaining > 0 ? `剩余 ${project.daysRemaining} 天` : '已完成'}
            </Text>
          </Space>
        </div>

        {/* 活动信息 */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {project.recentActivity}
          </Text>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* 操作按钮 */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(project);
            }}
          >
            查看详情 <RightOutlined />
          </Button>
          <Space size={8}>
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onExport?.(project);
              }}
            >
              导出
            </Button>
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onAddData?.(project);
              }}
            >
              添加
            </Button>
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => setActiveKeys(keys as string[])}
      expandIconPosition="end"
      style={{ background: 'transparent' }}
    >
      {projects.map((project) => {
        const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);
        return (
          <Panel
            key={project.id}
            header={renderPanelHeader(project)}
            style={{ 
              marginBottom: 8,
              backgroundColor: budgetColors.backgroundColor,
              borderColor: budgetColors.borderColor,
              borderRadius: 8,
              transition: 'all 0.3s ease'
            }}
          >
            {renderPanelContent(project)}
          </Panel>
        );
      })}
    </Collapse>
  );
};

export default CollapsibleProjectList;
